import React from 'react';
import { arrayOf, string } from 'prop-types';
import { flatten, memoize } from 'lodash-es';
import {
  schemaPartItemTypeValues,
  SurveySchemaEditorType,
  SurveySchemaPartEditorType, variableTypeValues
} from '../../../../constants/types';
import { getQuestionsWithResponseOptions } from '../../../../util';
import TabsContainer from '../../../TabsContainer';
import TabPane from '../../../TabPane';
import SchemaPartFeedbackPreview from '../../SchemaPartPreview/FeedbackPreview';
import SchemaPartQuestionsPreview from '../../SchemaPartPreview/QuestionsPreview';
import VariableRadarSummary from '../../../VariableRadarSummary';
import TranslatedHTMLText from '../../../TranslatedHTMLText';
import { getTranslationWithLocale, withLang, langType } from '../../../../i18n';

const mapResponseOptionsToVariableQuestions = memoize((schemaPart) => {
  const likertQuestionsWithResponseOptions = flatten(schemaPart.surveySchemaPartItems
    .filter((spi) => spi.type === schemaPartItemTypeValues.QUESTION_GROUP)
    .map(getQuestionsWithResponseOptions))
    .reduce((acc, question) => Object.assign(acc, { [question.id]: question }), {});

  const variables = schemaPart.variables
    .map((variable) => {
      const questions = variable.questions.map((q) => likertQuestionsWithResponseOptions[q.id]);
      return { ...variable, questions };
    });

  return { ...schemaPart, variables };
});

const randomValue = (maxValue) => Math.ceil(Math.random() * maxValue);

const buildVariableStatistics = (variable) => {
  const type = variable.responseCategories[0].absoluteCategory
    ? variableTypeValues.ABSOLUTE : variableTypeValues.RELATIVE;
  const responseOptionValues = variable.questions[0].responseOptions.map((ro) => ro.value);
  const maxResponseOptionValue = responseOptionValues[responseOptionValues.length - 1];
  const average = randomValue(maxResponseOptionValue);
  const userAverage = randomValue(maxResponseOptionValue);

  return {
    type,
    responseOptionValues,
    absoluteCategories: variable.absoluteCategories,
    name: variable.name,
    average,
    userAverage,
    minTotal: 0,
    maxTotal: 0,
    distribution: 0,
    order: variable.order
  };
};

const SurveySchemaPreviewTabs =
  ({
    surveySchema, schemaParts, extraSurveyPart, previewLang, translate: t
  }) => {
    const schemaPartsWithVariableQuestionResponseOptions =
      schemaParts.map(mapResponseOptionsToVariableQuestions);
    const variableStats = flatten(schemaPartsWithVariableQuestionResponseOptions
      .map((sp) => sp.variables)).map(buildVariableStatistics);

    return (
      <TabsContainer>
        <TabPane key="questions" label={t('surveyParts.questions')}>
          <div>
            {schemaParts.map((part, idx) => (
              <SchemaPartQuestionsPreview
                key={part.id}
                schemaPart={part}
                partNumber={idx + 1}
                lang={previewLang}
                translate={t}
              />
            ))}
            {extraSurveyPart && (
              <>
                <h2>{t('surveySchemaParts.extraSurveyPart')}</h2>
                <SchemaPartQuestionsPreview
                  schemaPart={extraSurveyPart}
                  partNumber={schemaParts.length + 1}
                  lang={previewLang}
                  translate={t}
                />
              </>
            )}
          </div>
        </TabPane>
        <TabPane
          key="feedback"
          label={t('surveySchemaParts.dimensions.dimensions')}
        >
          <div>
            <h2>{getTranslationWithLocale('surveySummary.summaryHeading', previewLang)}</h2>
            {/* WARNING: UH-670 - This is not accessible in case the html is not formated correctly */}
            <TranslatedHTMLText html={surveySchema.feedbackIntroduction} inLanguage={previewLang} />
            {variableStats.length > 0 && (
            <VariableRadarSummary
              variableStats={variableStats}
            />
            )}
            {schemaPartsWithVariableQuestionResponseOptions
              .filter((part) => part.variables && part.variables.length)
              .map((part) => (
                <SchemaPartFeedbackPreview
                  key={part.id}
                  schemaPart={part}
                  previewLang={previewLang}
                />
              ))}
          </div>
        </TabPane>
      </TabsContainer>
    );
  };

SurveySchemaPreviewTabs.propTypes = {
  surveySchema: SurveySchemaEditorType.isRequired,
  schemaParts: arrayOf(SurveySchemaPartEditorType).isRequired,
  extraSurveyPart: SurveySchemaPartEditorType,
  previewLang: string.isRequired,
  ...langType
};

SurveySchemaPreviewTabs.defaultProps = {
  extraSurveyPart: null
};

export default withLang(SurveySchemaPreviewTabs);
